import PropTypes from 'prop-types'
import React from 'react'
import { css } from 'styled-components'

import ButtonRoundedSt from 'components/ButtonRoundedSt'
import ButtonSuccessfulSt from 'components/ButtonSuccessfulSt'
import ControlAmount from 'components/ControlAmount'
import CostsStockAndBase from 'components/CostsStockAndBase'
import { RU } from 'constants/countries'
import { GOODS_TYPES } from 'constants/sales'
import { ResizeContext } from 'contexts/Resize'
import { H1Small as H1 } from 'styles/components/typography'

import bacteriaRu from './assets/bacteria-ru.svg'
import bacteria from './assets/bacteria.svg'
import {
  Wrapper,
  Inner,
  ContentWrapper,
  TextStyled,
  ButtonWrapper,
  RightBlock,
  RightBlockInner,
  CostStockWrapper,
  Bacteria,
  UpsaleBox,
  BoxWrapper,
} from './styles'

/**
 * Uncontrollable component of Upsale Card with wide behaviour
 * so you might use it on a page of the particular kit
 *
 * @param {object} props - component's props
 * @return {function} Function — UpsaleCardWide
 */
const UpsaleCardWide = ({
  isCartInProgress,
  boxesPath,
  buttonHandleClickBegin,
  buttonHandleClickEnd,
  buttonLinkClick,
  buttonLink,
  buttonAsPath,
  buttonLinkAs,
  buttonLinkTitle,
  buttonTitle,
  isFetching,
  costCurrency,
  costLocale,
  costValue,
  costRealValue,
  counterAmount,
  counterAmountIsActive,
  counterDescription,
  counterHandleDecrease,
  counterHandleIncrease,
  title,
  description,
  isVisible,
  productType,
  locale,
  isDisabled,
}) => {
  const { tablet, mobile } = React.useContext(ResizeContext)
  const buttonAnimAlign = tablet ? 'left' : 'center'
  const isWhite = productType === GOODS_TYPES.wgs
  const isRu = locale === RU
  let counterColumnClassName = 'col-12 col-md-6 col-lg-12 '
  const finalBacteria = isRu ? bacteriaRu : bacteria
  const buttonWidth = tablet ? undefined : mobile ? 200 : 150

  if (tablet) {
    counterColumnClassName += 'text-center '
  }

  if (costRealValue && costRealValue !== costValue) {
    counterColumnClassName += 'mb-lg-2'
  }

  const btnProps = React.useMemo(() => {
    if (isWhite) {
      return {
        bgStyles: css`
          background-color: ${p => p.theme.colors.white};
        `,
        textStyles: css`
          color: ${p => p.theme.colors.black};
        `,
      }
    }

    return {}
  }, [isWhite])
  const boxesSettings = React.useMemo(() => {
    const boxesRu = {
      wgs: {
        imageSizes: {
          xs: 900,
          md: 1305,
        },
        name: 'wgs-left-new',
      },
      dna: {
        imageSizes: {
          xs: 900,
          md: 1305,
        },
        name: 'dna-left-new',
      },
      biome: {
        imageSizes: {
          xs: 900,
          md: 1560,
        },
        name: 'biome-right-new',
      },
    }

    const boxesEu = {
      dna: {
        imageSizes: {
          xs: 900,
          md: 1305,
        },
        name: 'dna-left-new',
      },
      biome: {
        imageSizes: {
          xs: 900,
          md: 1560,
        },
        name: 'biome-right-v1',
      },
    }

    return isRu ? boxesRu : boxesEu
  }, [isRu])

  const controlsBlock =
    counterAmount &&
    counterHandleDecrease &&
    counterHandleIncrease &&
    counterDescription ? (
      <ControlAmount
        {...{ isWhite }}
        amount={counterAmount}
        isActive={counterAmountIsActive && !isDisabled}
        onMinusClick={counterHandleDecrease}
        onPlusClick={counterHandleIncrease}
        description={counterDescription}
        descriptionPosition="bottom"
      />
    ) : null
  const costBlock =
    !isDisabled && costCurrency && costLocale ? (
      <CostsStockAndBase
        {...{
          cost: costValue,
          realCost: costRealValue,
          intlCode: costLocale,
          currency: costCurrency,
          isFetching,
          isCenter: true,
          isWhite,
        }}
      />
    ) : null

  const buttonBlock = React.useMemo(() => {
    if (
      buttonHandleClickBegin &&
      buttonHandleClickEnd &&
      buttonTitle &&
      !isDisabled
    ) {
      return (
        <ButtonSuccessfulSt
          firstClickBeforeCb={buttonHandleClickBegin}
          firstClickCb={buttonHandleClickEnd}
          secondBtnAs={buttonLinkAs}
          secondClickTo={buttonLink}
          secondClickAsPath={buttonAsPath}
          secondClickText={buttonLinkTitle}
          secondClickCb={buttonLinkClick}
          animAlign={buttonAnimAlign}
          btnWidth={buttonWidth}
          {...btnProps}
        >
          {buttonTitle}
        </ButtonSuccessfulSt>
      )
    }

    return (
      <ButtonRoundedSt
        as={buttonLinkAs}
        to={buttonLink}
        isDisabled={isDisabled}
        asPath={buttonAsPath}
        {...btnProps}
      >
        {buttonLinkTitle}
      </ButtonRoundedSt>
    )
  }, [
    btnProps,
    buttonHandleClickBegin,
    buttonHandleClickEnd,
    buttonLinkClick,
    buttonLinkAs,
    buttonAsPath,
    buttonLink,
    buttonLinkTitle,
    buttonAnimAlign,
    buttonWidth,
    buttonTitle,
    isDisabled,
  ])

  return (
    <Wrapper>
      <Inner withBg={productType !== GOODS_TYPES.wgs} className="pt-4 pb-md-4">
        <BoxWrapper {...{ productType }}>
          {isVisible && (
            <UpsaleBox
              {...{ isRu, productType }}
              alt={`Kit: ${productType}`}
              imageSizes={boxesSettings[productType].imageSizes}
              image={`${boxesPath}/${boxesSettings[productType].name}`}
              imageType="png"
              fitOption="contain"
            />
          )}
          {productType === GOODS_TYPES.biome && isVisible && (
            <Bacteria
              {...{ isRu }}
              alt="bacteria-fishing"
              src={finalBacteria}
            />
          )}
        </BoxWrapper>

        <ContentWrapper isTextWhite={isWhite}>
          <div className="d-inline-block">
            <H1
              as="h2"
              className="mb-md-05"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <TextStyled
              className="max-w--80p mb-md-2"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        </ContentWrapper>

        <RightBlock className="row">
          <div className={counterColumnClassName}>{controlsBlock}</div>

          <RightBlockInner
            {...{ isWhite }}
            className="col-12 col-md-6 col-lg-12"
          >
            <CostStockWrapper className="mt-4 mt-md-0">
              {costBlock}
            </CostStockWrapper>
            <ButtonWrapper className="pt-md-2">
              {!isFetching && !isCartInProgress && buttonBlock}
            </ButtonWrapper>
          </RightBlockInner>
        </RightBlock>
      </Inner>
    </Wrapper>
  )
}

UpsaleCardWide.propTypes = {
  /** Is cart info loading from server */
  isCartInProgress: PropTypes.bool.isRequired,

  /** Localized path to boxes */
  boxesPath: PropTypes.string.isRequired,

  /** Link of button at the successful state */
  buttonLink: PropTypes.string.isRequired,

  /** Title of button at the successful state */
  buttonLinkTitle: PropTypes.string.isRequired,

  /** String for client-routing for Link component */
  buttonAsPath: PropTypes.string,

  /** Title of a button at the begin state */
  buttonTitle: PropTypes.string,

  /** Function called right after button was clicked */
  buttonHandleClickBegin: PropTypes.func,

  /** Function called right after button successfully animated */
  buttonHandleClickEnd: PropTypes.func,

  /** Type of link button (to work in the styleguide) */
  buttonLinkAs: PropTypes.string,

  /** Title to show when to add products to cart */
  buttonSuccessfulTitle: PropTypes.string,

  /** Callback after redirection **/
  buttonLinkClick: PropTypes.func,

  /** Code of currency to show cost of product */
  costCurrency: PropTypes.string,

  /** Locale to use internationalization for cost rendering */
  costLocale: PropTypes.string,

  /** Actual cost of product */
  costValue: PropTypes.number,

  /** Real cost of product */
  costRealValue: PropTypes.number,

  /** Amount of goods */
  counterAmount: PropTypes.number,

  /** Flag to make counter active/blocked */
  counterAmountIsActive: PropTypes.bool,

  /** Description near the counter */
  counterDescription: PropTypes.string,

  /** Decrease amount of products callback */
  counterHandleDecrease: PropTypes.func,

  /** Increase amount of products callback */
  counterHandleIncrease: PropTypes.func,

  /** Language to show right boxes */
  locale: PropTypes.string.isRequired,

  /** Are sales disabled **/
  isDisabled: PropTypes.bool.isRequired,

  /** Title of the card */
  title: PropTypes.string.isRequired,

  /** Description to show in below a title of the card*/
  description: PropTypes.string,

  /** Do we need to render the bottom button and price */
  isFetching: PropTypes.bool,

  /** Is it tablet */
  isVisible: PropTypes.bool.isRequired,

  /** Type of product */
  productType: PropTypes.string.isRequired,
}

UpsaleCardWide.defaultProps = {
  buttonLinkAs: 'Link',
  isFetching: true,
}

export default UpsaleCardWide
